// Loads the Pulse runtime (the UMD build copied from the pulse-player repository into
// public/pulse/) once, and counts WebGL draw calls per canvas for the perf HUD. Pulse's
// player.perf does not report draw calls, so the page counts them the way the repository's
// benchmark does: by wrapping the WebGL2 draw entry points. The wrapper adds one Map lookup
// per call.

import { RUNTIME_URL } from './vendor.js';
let loading = null;

export function loadRuntime() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.Pulse) return Promise.resolve(window.Pulse);
  if (loading) return loading;
  instrumentDraws();
  loading = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = RUNTIME_URL;
    s.async = true;
    s.onload = () => (window.Pulse ? resolve(window.Pulse) : reject(new Error('Pulse did not load')));
    s.onerror = () => { loading = null; reject(new Error('Pulse did not load')); };
    document.head.appendChild(s);
  });
  return loading;
}

/** Whether this browser has WebGL2 at all (checked once, without keeping a context). */
export function hasWebGL2() {
  try {
    const c = document.createElement('canvas');
    const gl = c.getContext('webgl2');
    if (gl) gl.getExtension('WEBGL_lose_context')?.loseContext();
    return !!gl;
  } catch {
    return false;
  }
}

const counters = new WeakMap(); // canvas -> {calls}
let instrumented = false;

function instrumentDraws() {
  if (instrumented || typeof WebGL2RenderingContext === 'undefined') return;
  instrumented = true;
  const P = WebGL2RenderingContext.prototype;
  for (const name of ['drawArrays', 'drawElements', 'drawArraysInstanced', 'drawElementsInstanced', 'drawRangeElements']) {
    const orig = P[name];
    if (!orig) continue;
    P[name] = function (...args) {
      const c = counters.get(this.canvas);
      if (c) c.calls++;
      return orig.apply(this, args);
    };
  }
}

/**
 * Track a player's drawn frames and the GL draw calls they make. Returns a sampler:
 * sample() -> {frames, drawsPerFrame} since the previous sample.
 */
export function trackPlayer(player) {
  const state = { frames: 0, calls: 0, canvas: null };
  const origRender = player.render;
  player.render = function (...args) {
    const canvas = player.canvas || player._canvas || null;
    if (canvas && canvas !== state.canvas) {
      state.canvas = canvas;
      counters.set(canvas, state);
    }
    state.frames++;
    return origRender.apply(this, args);
  };
  let last = { frames: 0, calls: 0 };
  return {
    sample() {
      const f = state.frames - last.frames;
      const c = state.calls - last.calls;
      last = { frames: state.frames, calls: state.calls };
      return { frames: f, drawsPerFrame: f ? c / f : 0 };
    },
    bindCanvas(canvas) {
      if (canvas) { state.canvas = canvas; counters.set(canvas, state); }
    },
  };
}
